import classNames from 'classnames'
import { type StructuredText as StructuredTextType } from 'datocms-structured-text-utils'
import { graphql } from 'gatsby'
import React from 'react'
import { Gallery } from 'react-photoswipe-gallery'
import { v4 as uuidv4 } from 'uuid'

import Button from '@/components/button/button'
import Image from '@/components/image/image'
import { ImageViewModal } from '@/components/image/image-view-modal'
import Link from '@/components/link/link'
import StructuredTextDefault from '@/components/structured-text/structured-text-default'
import Player from '@/components/video/player/player'
import { ViewModal } from '@/components/view-modal/view-modal'
import { isTwoColumns, splitBody } from '@/utils/body'
import {
  getImageAspectRatioClass,
  ImageAspectRatio
} from '@/utils/get-image-aspect-ratio-class'
import { generateUrlFromNode } from '@/utils/url'

import * as styles from './agenda-item.module.scss'

const AgendaItem: React.FC<Queries.AgendaItemFragment> = ({
  dateAndTime,
  body,
  person,
  internalMedia,
  externalMedia,
  tags
}) => {
  const multipleAuthors = person !== null && person?.length > 1
  const countAuthors = person?.length
  const classColumnText = multipleAuthors
    ? `col-12`
    : countAuthors === 1
      ? `col-xl-6 pe-xl-5 col-md-12`
      : `col-12`
  const classColumnPerson = multipleAuthors
    ? `col-12`
    : `col-xl-6 ps-xl-5 col-md-7 col-12 mt-4 mt-lg-0`

  let bodies: StructuredTextType[] = []
  let bodyClass = ''
  if (
    body !== null &&
    isTwoColumns(body as unknown as StructuredTextType, 'thematicBreak')
  ) {
    bodies = splitBody(body as unknown as StructuredTextType, 'thematicBreak')
    bodyClass = 'row gx-5'
  } else {
    bodies = [body] as unknown as StructuredTextType[]
  }

  return (
    <div className="container">
      <div className={styles.agendaItem}>
        {dateAndTime && <h3 className={styles.dateBox}>{dateAndTime}</h3>}
        <div className={`mt-3 mb-1 ${styles.btns}`}>
          {tags !== null &&
            tags.length > 0 &&
            tags.map((tag) => {
              const urlTopic = tag ? generateUrlFromNode(tag) : undefined
              if (tag && urlTopic) {
                return (
                  <Button
                    key={tag?.id}
                    to={urlTopic}
                    title={tag?.externalName ?? ''}
                    isBgDark={true}
                  />
                )
              } else {
                return null
              }
            })}
        </div>
        {/* no medias */}
        {internalMedia?.length == 0 && !externalMedia && (
          <div className="row">
            <div className={classNames(classColumnText)}>
              <div className={classNames(styles.textContent, bodyClass)}>
                {bodies.length > 1 &&
                  bodies.map((item: StructuredTextType) => {
                    return (
                      <div className="col-md-6" key={uuidv4()}>
                        <StructuredTextDefault content={item} />
                      </div>
                    )
                  })}
                {bodies.length === 1 && (
                  <StructuredTextDefault
                    content={body as unknown as StructuredTextType}
                  />
                )}
              </div>
            </div>
            <div className={classNames(classColumnPerson)}>
              <div className={multipleAuthors ? styles.multipleAuthors : ''}>
                {person?.map((item) => {
                  return (
                    <Person
                      key={item?.id}
                      person={item}
                      className={multipleAuthors ? 'flex-column mt-3' : 'mt-3'}
                    />
                  )
                })}
              </div>
            </div>
          </div>
        )}
        {/* with media */}
        {((internalMedia !== null && internalMedia?.length > 0) ||
          externalMedia) && (
          <div className="row">
            <div
              className={`col-xl-6 pe-xl-5 col-md-7 ${styles.colContent} ${styles.textContent}`}
            >
              <StructuredTextDefault
                content={body as unknown as StructuredTextType}
              />
              <div
                className={multipleAuthors ? styles.multipleAuthors : 'pt-1'}
              >
                {person?.map((item) => {
                  return (
                    <Person
                      key={item?.id}
                      person={item}
                      className={multipleAuthors ? 'flex-column mt-3' : 'mt-3'}
                    />
                  )
                })}
              </div>
            </div>
            <div className="col-xl-6 ps-xl-5 col-md-5 mt-4 mt-lg-0">
              <Gallery>
                <div className="row">
                  {internalMedia?.map((item) => {
                    return (
                      <div
                        className="col-12 col-sm-6 col-md-12"
                        key={item.originalId}
                      >
                        <InternalMedia
                          item={item as InternalMediaProps['item']}
                        />
                      </div>
                    )
                  })}
                  {externalMedia !== null && (
                    <ExternalMedia
                      item={externalMedia as ExternalMediaProps['item']}
                    />
                  )}
                </div>
              </Gallery>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

type PersonProps = {
  person: {
    __typename: 'DatoCmsGctPerson'
    id: string
    firstName: string | null
    secondName: string | null
    disableProfilePage: boolean | null
    job: string | null
    photo: {
      url: string | null
      alt: string | null
      gatsbyImageData: Record<string, unknown> | null
    } | null
    company: {
      externalName: string | null
      url: string | null
      logo: {
        url: string | null
        gatsbyImageData: Record<string, unknown> | null
        width: number | null
        height: number | null
      } | null
      logoNegative: {
        url: string | null
        gatsbyImageData: Record<string, unknown> | null
        width: number | null
        height: number | null
      } | null
    } | null
  } | null
  className?: string
}

const Person: React.FC<PersonProps> = ({ person, className }) => {
  if (person === undefined || person === null) return null
  const { id, firstName, secondName, photo, job, disableProfilePage, company } =
    person
  let aspectRatioClass = ''

  if (company) {
    const logoToUse = company.logo ?? company.logoNegative
    if (logoToUse) {
      aspectRatioClass = getImageAspectRatioClass(
        styles,
        logoToUse as ImageAspectRatio
      )
    }
  }
  const url = disableProfilePage === true ? '' : generateUrlFromNode(person)

  return (
    <div key={id} className={classNames(styles.personBlock, className)}>
      <div className={styles.colImage}>
        <Link to={url}>
          <Image image={photo} />
        </Link>
      </div>
      <div>
        <div className={`pb-2 ${styles.namePerson}`}>
          <Link to={url}>
            {firstName} {secondName}
          </Link>
        </div>
        <div className={`pb-3 ${styles.job}`}>{job}</div>
        {company !== null && (
          <div className={styles.logoBox}>
            <Image
              className={`${styles.companyLogo} ${aspectRatioClass}`}
              image={company?.logoNegative ?? company?.logo}
            />
          </div>
        )}
      </div>
    </div>
  )
}

type InternalMediaProps = {
  item: {
    originalId: string
    isImage: boolean | null
    alt: string | null
    title: string | null
    fluid: {
      src: string
    } | null
    fullImageFluid: {
      src: string
      srcSet: string
      height: number | null
      width: number | null
    } | null
    image: {
      images: {
        sources: string[]
        fallback: {
          src: string
          srcSet: string
          sizes: string
        }
      }
      layout: string
      placeholder: {
        fallback: string
      }
      width: number | null
      height: number | null
    } | null
    video: {
      thumbnailUrl: string
      mp4Url: string
    } | null
  }
}

type ExternalMediaProps = {
  item: {
    providerUid: string
    url: string
    title: string
    thumbnailUrl: string
    width: number | null
    height: number | null
  }
}

const InternalMedia: React.FC<InternalMediaProps> = ({ item }) => {
  if (item.isImage === true) {
    return (
      <div className="mb-4" key={item.originalId}>
        <ImageViewModal
          image={{
            alt: item.alt,
            fluid: item.fluid
          }}
          fullImage={{
            alt: item.alt,
            fluid: item.fullImageFluid
          }}
        />
      </div>
    )
  } else {
    const imageThumb = item?.video?.thumbnailUrl
      ? item?.video?.thumbnailUrl + '?width=450&height=300&fit_mode=crop'
      : ''
    return (
      <div className="mb-4">
        <ViewModal
          content={
            <Player
              video={item?.video?.mp4Url}
              autoPlay={true}
              preview={null}
            />
          }
          preview={
            <span className={styles.playVideo}>
              <img
                src={imageThumb}
                alt={item?.title ?? ''}
                style={{
                  cursor: 'pointer',
                  objectFit: 'contain',
                  width: '100%',
                  maxHeight: '100%'
                }}
              />
            </span>
          }
        />
      </div>
    )
  }
}

const ExternalMedia: React.FC<ExternalMediaProps> = ({ item }) => {
  return (
    <div>
      <ViewModal
        content={<Player video={item?.url} autoPlay={true} preview={null} />}
        preview={
          <span className={styles.playVideo}>
            <img
              src={item?.thumbnailUrl ?? ''}
              alt={item?.title ?? ''}
              style={{
                cursor: 'pointer',
                objectFit: 'contain',
                width: '100%',
                maxHeight: '100%'
              }}
            />
          </span>
        }
      />
      <div className={styles.captionVideo}>{item?.title}</div>
    </div>
  )
}

export default AgendaItem

export const query = graphql`
  fragment AgendaItem on DatoCmsAgendaItem {
    __typename
    id
    title
    dateAndTime
    tags {
      __typename
      ... on DatoCmsPctTag {
        ...TagReference
      }
      ... on DatoCmsPctTopic {
        ...TopicReference
      }
    }
    body {
      value
      blocks {
        __typename
        ... on DatoCmsImage {
          ...ImageInline
        }
        ... on DatoCmsVideo {
          ...VideoInline
        }
      }
    }
    person {
      ... on DatoCmsGctPerson {
        __typename

        id
        firstName
        secondName
        job
        disableProfilePage
        company {
          __typename
          externalName
          url
          logo {
            gatsbyImageData
            url
            width
            height
          }
          logoNegative {
            gatsbyImageData
            url
            width
            height
          }
        }
        photo {
          __typename
          url
          alt
          gatsbyImageData(
            width: 310
            height: 310
            imgixParams: { w: "310", h: "310", fit: "crop", crop: "focalpoint" }
          )
        }
      }
    }
    internalMedia {
      originalId
      isImage
      alt
      title
      video {
        thumbnailUrl
        mp4Url
      }
      fluid(imgixParams: { w: "450", h: "300", fit: "crop", crop: "faces" }) {
        src
      }
      fullImageFluid: fluid(imgixParams: { w: "1800" }) {
        src
        srcSet
        height
        width
      }
      image: gatsbyImageData(width: 450)
    }
    externalMedia {
      providerUid
      url
      title
      thumbnailUrl
      width
      height
    }
  }
`
